// =============================================================================
// MOBRES ETKILESIM ISTEMCISI
//
// Bu dosya HER QR MENU PROJESINE oldugu gibi kopyalanir (src/utils/ altina).
// Tek yapilandirma: .env icindeki VITE_STATS_API.
//
// Kural: etkilesim hatalari menuyu ASLA bozmaz. Ag hatasi, kapali ozellik veya
// eksik yapilandirma durumunda menu calismaya devam eder, yalnizca begeni /
// yorum arayuzu sessizce devre disi kalir.
// =============================================================================

// API adresi.
//
// Bos birakilirsa menunun kendi adresi altindaki /api denenir.
// Not: Projelerin bir kismi Vite, bir kismi Create React App kullaniyor.
// Vite'ta ayarlar import.meta.env'den, CRA'da process.env'den gelir; ikisi de
// denenir ki tek dosya her projede calissin.
function readEnv() {
  // import.meta yalnizca ESM'de tanimli; CRA derlemesinde erisim hata verir.
  try {
    if (import.meta && import.meta.env) return import.meta.env;
  } catch {
    /* CRA / CommonJS */
  }
  if (typeof process !== "undefined" && process.env) {
    return {
      DEV: process.env.NODE_ENV !== "production",
      VITE_STATS_API: process.env.REACT_APP_STATS_API || process.env.VITE_STATS_API,
    };
  }
  return {};
}

const ENV = readEnv();

// URETIMDE: .env icindeki VITE_STATS_API (CRA'da REACT_APP_STATS_API).
// GELISTIRMEDE: mutlak adres BILEREK yok sayilir, gorece "/api" kullanilir.
// Boylece istek Vite proxy'sine / CRA proxy'sine duser, tarayici ayni origin
// gorur ve CORS'a hic takilmayiz.
const API_BASE = (ENV.DEV ? "/api" : (ENV.VITE_STATS_API || "/api"))
  .replace(/\/+$/, "");

// Ziyaretci kimligi cihazda uretilir ve saklanir. Sunucu kim oldugunu bilmez;
// yalnizca "ayni cihaz mi" sorusunu cevaplamak icin kullanilir.
const VISITOR_KEY = "mobresVisitorId";

// Cihazin begendikleri restorana GORE ayrilir: ayni urun kodu farkli
// restoranlarda karismasin.
const likesKey = (restaurantId) => `mobresLikes:${restaurantId}`;

// --- Ziyaretci kimligi -------------------------------------------------------

export function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      // Not: globalThis yerine window kullaniliyor; Create React App'in eski
      // ESLint yapilandirmasi globalThis'i tanimiyor ve derlemeyi durduruyor.
      const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
      id = cryptoApi?.randomUUID?.() || `visitor-${Date.now()}-${Math.random()}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    // Depolama kapaliysa (gizli sekme vb.) oturumluk kimlik uretilir.
    return `session-${Date.now()}-${Math.random()}`;
  }
}

// --- Cihazdaki begeniler -----------------------------------------------------

export function readLocalLikes(restaurantId) {
  try {
    const raw = localStorage.getItem(likesKey(restaurantId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writeLocalLike(restaurantId, productId, liked) {
  try {
    const map = readLocalLikes(restaurantId);
    if (liked) map[productId] = true;
    else delete map[productId];
    localStorage.setItem(likesKey(restaurantId), JSON.stringify(map));
  } catch {
    /* depolama yoksa yoksay */
  }
}

// --- Ortak istek yardimcisi --------------------------------------------------

async function jsonRequest(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });

  if (!response.ok) {
    let payload = null;
    try { payload = await response.json(); } catch { /* govde yok */ }
    const error = new Error(payload?.message || `Istek basarisiz (${response.status})`);
    error.status = response.status;
    error.code = payload?.code;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
}

// --- Ozellik bayraklari ------------------------------------------------------

/**
 * Restoranin hangi ozelliklerinin acik oldugunu doner.
 * Menu acilirken BIR KEZ cagrilir; kapali ozelliklerin arayuzu hic cizilmez.
 *
 * Hata durumunda guvenli varsayilan doner: begeni acik, yorum ve istatistik
 * kapali. Boylece API erisilemez olsa bile menu bozulmaz.
 */
export async function fetchSettings(restaurantId) {
  if (!restaurantId) return { likes: false, reviews: false, analytics: false };
  try {
    const data = await jsonRequest(`/tenants/${encodeURIComponent(restaurantId)}/settings`);
    return {
      likes: !!data?.likes,
      reviews: !!data?.reviews,
      analytics: !!data?.analytics,
      displayName: data?.displayName ?? null,
    };
  } catch {
    return { likes: true, reviews: false, analytics: false, displayName: null };
  }
}

// --- Herkese acik etkilesim verisi -------------------------------------------

/**
 * Menunun ihtiyaci olan tum veriyi TEK istekte getirir:
 * { settings, products: { [URUN_KOD]: { likes, reviewCount, averageRating, reviews } } }
 *
 * Yuzlerce urun icin yuzlerce istek atilmaz.
 */
export async function fetchEngagements(restaurantId) {
  if (!restaurantId) return { settings: null, products: {} };
  try {
    const data = await jsonRequest(`/engagements/public/${encodeURIComponent(restaurantId)}`);
    return {
      settings: data?.settings ?? null,
      products: data?.products ?? {},
    };
  } catch {
    return { settings: null, products: {} };
  }
}

// --- Begeni ------------------------------------------------------------------

/**
 * Begeniyi acar/kapatir. Sunucu guncel toplami doner.
 * Hata firlatir; cagiran iyimser guncellemeyi geri alabilsin.
 */
export async function toggleLike(restaurantId, productId) {
  return jsonRequest("/engagements/like", {
    method: "POST",
    body: JSON.stringify({ restaurantId, productId, visitorId: getVisitorId() }),
  });
}

// --- Yorum -------------------------------------------------------------------

/** Yorum gonderir. Yorum moderasyondan gecmeden menude gorunmez. */
export async function submitReview(restaurantId, productId, { rating, comment, name, phone }) {
  return jsonRequest("/engagements/review", {
    method: "POST",
    body: JSON.stringify({
      restaurantId,
      productId,
      visitorId: getVisitorId(),
      rating,
      comment,
      name: name || null,
      phone: phone || null,
    }),
  });
}

// --- Goruntulenme ------------------------------------------------------------

// Ayni varlik icin kisa surede tekrarlanan kayitlar engellenir: kullanici
// listede asagi yukari kaydirdikca ayni urun defalarca sayilmasin.
const VIEW_DEDUPE_MS = 8000;
const recentViews = new Map();

/**
 * Goruntulenme bildirir. Sessizdir: istatistik kapaliysa veya ag hatasi
 * olursa hicbir sey olmaz, menu etkilenmez.
 */
export function trackView(restaurantId, entityType, entityId) {
  if (!restaurantId || !entityId) return;

  const key = `${entityType}:${entityId}`;
  const now = Date.now();
  const last = recentViews.get(key);
  if (last && now - last < VIEW_DEDUPE_MS) return;
  recentViews.set(key, now);

  jsonRequest("/analytics/view", {
    method: "POST",
    body: JSON.stringify({ restaurantId, entityType, entityId, visitorId: getVisitorId() }),
  }).catch(() => null);
}
